import React from 'react'
import { Icon } from './icons'

// Inject toast/tooltip keyframes once.
if (typeof document !== 'undefined' && !document.getElementById('tn-feedback-styles')) {
  const s = document.createElement('style')
  s.id = 'tn-feedback-styles'
  s.textContent = `
    @keyframes tn-toast-in  { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    @keyframes tn-toast-out { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
    @keyframes tn-tip-in    { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
  `
  document.head.appendChild(s)
}

// ─── Banner ─────────────────────────────────────────────────────────
const BANNER_TONES = {
  info:    { fg: 'var(--st-info)',    bg: 'var(--st-info-bg)',    icon: 'msg' as const },
  warning: { fg: 'var(--st-warning)', bg: 'var(--st-warning-bg)', icon: 'flag' as const },
  error:   { fg: 'var(--st-error)',   bg: 'var(--st-error-bg)',   icon: 'x' as const },
  success: { fg: 'var(--st-success)', bg: 'var(--st-success-bg)', icon: 'check' as const },
}

export interface BannerProps {
  tone?: keyof typeof BANNER_TONES
  icon?: string
  action?: React.ReactNode
  onDismiss?: (() => void) | null
  children?: React.ReactNode
  style?: React.CSSProperties
}

export function Banner({ tone = 'info', icon, action = null, onDismiss = null, children, style = {} }: BannerProps) {
  const t = BANNER_TONES[tone]
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderRadius: 'var(--r-md)',
      background: t.bg, color: t.fg,
      border: `1px solid ${t.fg}`,
      fontFamily: 'var(--font-sans)', fontSize: 13.5, lineHeight: 1.4,
      ...style,
    }}>
      <Icon name={icon || t.icon} size={16} sw={1.8}/>
      <span style={{ flex: 1, color: 'var(--text-primary)' }}>{children}</span>
      {action}
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Dismiss" style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 4, color: t.fg, display: 'inline-flex',
        }}>
          <Icon name="x" size={14} sw={2}/>
        </button>
      )}
    </div>
  )
}

// ─── Toast (imperative API) ──────────────────────────────────────────
interface ToastItem {
  id: number
  tone: keyof typeof BANNER_TONES
  message: string
  icon?: string
  ttl?: number
}

const _toastListeners = new Set<(ev: { type: 'add'; item: ToastItem } | { type: 'remove'; id: number }) => void>()
let _toastSeq = 0

export const toast = {
  show: (opts: Partial<ToastItem> & { message: string }): number => {
    const id = ++_toastSeq
    const item: ToastItem = { id, ttl: 4000, tone: 'info', ...opts }
    _toastListeners.forEach((fn) => fn({ type: 'add', item }))
    if (item.ttl !== Infinity) setTimeout(() => toast.dismiss(id), item.ttl)
    return id
  },
  dismiss: (id: number) => { _toastListeners.forEach((fn) => fn({ type: 'remove', id })) },
  info:    (msg: string, opts?: Partial<ToastItem>) => toast.show({ tone: 'info',    message: msg, ...opts }),
  success: (msg: string, opts?: Partial<ToastItem>) => toast.show({ tone: 'success', message: msg, ...opts }),
  warning: (msg: string, opts?: Partial<ToastItem>) => toast.show({ tone: 'warning', message: msg, ...opts }),
  error:   (msg: string, opts?: Partial<ToastItem>) => toast.show({ tone: 'error',   message: msg, ...opts }),
}

export function ToastHost() {
  const [items, setItems] = React.useState<ToastItem[]>([])
  React.useEffect(() => {
    const fn = (ev: { type: 'add'; item: ToastItem } | { type: 'remove'; id: number }) => {
      if (ev.type === 'add')    setItems((xs) => [...xs, ev.item])
      if (ev.type === 'remove') setItems((xs) => xs.filter((x) => x.id !== ev.id))
    }
    _toastListeners.add(fn)
    return () => { _toastListeners.delete(fn) }
  }, [])
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360,
    }}>
      {items.map((it) => {
        const t = BANNER_TONES[it.tone] || BANNER_TONES.info
        return (
          <div key={it.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px', borderRadius: 'var(--r-md)',
            background: 'var(--bg-surface)', border: `1px solid ${t.fg}`,
            boxShadow: 'var(--shadow-2)',
            fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text-primary)',
            animation: 'tn-toast-in var(--dur-base) var(--ease-out)', minWidth: 240,
          }}>
            <span style={{ color: t.fg, display: 'inline-flex', flexShrink: 0, paddingTop: 1 }}>
              <Icon name={it.icon || t.icon} size={16} sw={1.8}/>
            </span>
            <span style={{ flex: 1, lineHeight: 1.45 }}>{it.message}</span>
            <button onClick={() => toast.dismiss(it.id)} aria-label="Dismiss" style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 2, color: 'var(--text-muted)', display: 'inline-flex', marginLeft: 4,
            }}>
              <Icon name="x" size={12} sw={2}/>
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ─── Tooltip ────────────────────────────────────────────────────────
export interface TooltipProps {
  label: string
  children?: React.ReactNode
  side?: 'top' | 'bottom'
  style?: React.CSSProperties
}

export function Tooltip({ label, children, side = 'top', style = {} }: TooltipProps) {
  const [show, setShow] = React.useState(false)
  return (
    <span
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)} onBlur={() => setShow(false)}
      style={{ position: 'relative', display: 'inline-flex', ...style }}>
      {children}
      {show && (
        <span role="tooltip" style={{
          position: 'absolute',
          [side === 'top' ? 'bottom' : 'top']: 'calc(100% + 6px)',
          left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-elevated)', color: 'var(--text-primary)',
          padding: '4px 8px', borderRadius: 'var(--r-xs)',
          fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 500,
          whiteSpace: 'nowrap', pointerEvents: 'none',
          border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-1)',
          animation: 'tn-tip-in var(--dur-fast) var(--ease-out)', zIndex: 1000,
        }}>{label}</span>
      )}
    </span>
  )
}

// ─── Kbd ────────────────────────────────────────────────────────────
export interface KbdProps {
  children?: React.ReactNode
  style?: React.CSSProperties
}

export function Kbd({ children, style = {} }: KbdProps) {
  return (
    <kbd style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 18, height: 18, padding: '0 5px',
      background: 'var(--kbd-bg)', border: '1px solid var(--border)', borderBottomWidth: 2,
      borderRadius: 4, fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 600,
      color: 'var(--text-secondary)', letterSpacing: 0, ...style,
    }}>{children}</kbd>
  )
}
